import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import "./AdminUserAnalytics.css";

const API_URL = "https://lttsbackend.axiomos.in/user/analytics";

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleString("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
  });
};

const formatMonthLabel = (item) => {
  const year = item?._id?.year;
  const month = item?._id?.month;
  if (!year || !month) return "—";

  const date = new Date(year, month - 1, 1);
  return date.toLocaleString("en-IN", {
    month: "short",
    year: "numeric",
  });
};

const getMaxValue = (arr, key) => {
  if (!Array.isArray(arr) || !arr.length) return 0;
  return Math.max(...arr.map((item) => Number(item?.[key]) || 0), 0);
};

const StatCard = ({ label, value }) => (
  <div className="aua-stat-card">
    <div className="aua-stat-value">{value ?? 0}</div>
    <div className="aua-stat-label">{label}</div>
  </div>
);

const BarList = ({
  title,
  data = [],
  valueKey = "count",
  labelKey = "_id",
  emptyText = "No data found",
}) => {
  const max = getMaxValue(data, valueKey);

  return (
    <div className="aua-panel">
      <div className="aua-panel-header">
        <h3>{title}</h3>
      </div>

      {!data.length ? (
        <div className="aua-empty">{emptyText}</div>
      ) : (
        <div className="aua-bar-list">
          {data.map((item, index) => {
            const value = Number(item?.[valueKey]) || 0;
            const label = item?.[labelKey] || "Unknown";
            const width = max ? `${(value / max) * 100}%` : "0%";

            return (
              <div className="aua-bar-row" key={`${label}-${index}`}>
                <div className="aua-bar-row-top">
                  <span className="aua-bar-label">{label}</span>
                  <span className="aua-bar-value">{value}</span>
                </div>
                <div className="aua-bar-track">
                  <div className="aua-bar-fill" style={{ width }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const SimpleTable = ({
  title,
  columns = [],
  rows = [],
  emptyText = "No records found",
}) => (
  <div className="aua-panel">
    <div className="aua-panel-header">
      <h3>{title}</h3>
    </div>

    {!rows.length ? (
      <div className="aua-empty">{emptyText}</div>
    ) : (
      <div className="aua-table-wrap">
        <table className="aua-table">
          <thead>
            <tr>
              {columns.map((col) => (
                <th key={col.key}>{col.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => (
              <tr key={row._id || row.name || rowIndex}>
                {columns.map((col) => (
                  <td key={col.key}>
                    {col.render ? col.render(row) : (row[col.key] ?? "—")}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

const GrowthChart = ({ data = [] }) => {
  const max = useMemo(() => getMaxValue(data, "count"), [data]);

  return (
    <div className="aua-panel">
      <div className="aua-panel-header">
        <h3>User Growth</h3>
      </div>

      {!data.length ? (
        <div className="aua-empty">No growth data found</div>
      ) : (
        <div className="aua-growth-chart">
          {data.map((item, index) => {
            const count = Number(item?.count) || 0;
            const height = max ? `${Math.max((count / max) * 100, 6)}%` : "6%";

            return (
              <div
                className="aua-growth-item"
                key={`${item?._id?.year}-${item?._id?.month}-${index}`}
              >
                <div className="aua-growth-bar-wrap">
                  <div className="aua-growth-count">{count}</div>
                  <div className="aua-growth-bar" style={{ height }} />
                </div>
                <div className="aua-growth-label">{formatMonthLabel(item)}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const AdminUserAnalytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await axios.get(API_URL);
      setAnalytics(response?.data?.data || null);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to load analytics",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const overview = analytics?.overview || {};
  const roleDistribution = analytics?.roleDistribution || [];
  const projectUsage = analytics?.projectUsage || [];
  const topUsers = analytics?.topUsers || [];
  const recentLogins = analytics?.recentLogins || [];
  const userGrowth = analytics?.userGrowth || [];

  return (
    <div className="aua-page">
      {loading ? (
        <div className="aua-state-card">Loading analytics...</div>
      ) : error ? (
        <div className="aua-state-card aua-error">
          <div>{error}</div>
          <button className="aua-retry-btn" onClick={fetchAnalytics}>
            Retry
          </button>
        </div>
      ) : (
        <>
          <div className="aua-stats-grid">
            <StatCard label="Total Users" value={overview.totalUsers} />
            <StatCard label="Active Today" value={overview.activeToday} />
            <StatCard label="Active This Week" value={overview.activeWeek} />
            <StatCard label="Active This Month" value={overview.activeMonth} />
            <StatCard label="Inactive Users" value={overview.inactiveUsers} />
          </div>
        </>
      )}
    </div>
  );
};

export default AdminUserAnalytics;
